import { useEffect } from 'react';
import { Discount } from '../XV2';
import { useApiQuery } from '@orderx/lab/xr/http';
import { apiMeDiscountPost } from '../api/me.discount.[post]';
import { withoutRelations } from '../util/withoutRelations';
import moment from 'moment';
import { createStore$ } from '../createStore$';

type DiscountId = number;
type DateString = string;

const store = createStore$<{
    discountIds?: number[];
    discountIdClaimedAt?: Record<DiscountId, DateString>;
}>({
    cacheKey: 'discounts',
    initialState: {
        discountIds: [],
        discountIdClaimedAt: {},
    },
});

const discountState = createStore$<{
    discountByProductId: Record<string, Discount>;
    discounts: Discount[];
}>({
    initialState: {
        discountByProductId: {},
        discounts: [],
    },
});

export function useDiscount() {
    const [state, setState] = store.useState();
    const { discountIds = [] } = state;
    const [discountData, setDiscountData] = discountState.useState();

    const apiQueryDiscounts = useApiQuery(async () => {
        const _discounts: Discount[] = await apiMeDiscountPost({
            body: { discountIds },
        });

        const discounts = _discounts.filter((discount) => {
            const claimedAt = state.discountIdClaimedAt?.[discount.id];

            if (discount.revokedAt && claimedAt) {
                const isRevoked = moment(claimedAt).isBefore(discount.revokedAt);

                if (isRevoked) {
                    return false;
                }
            }

            if (claimedAt && discount.hoursValidFor > 0) {
                return moment().subtract(discount.hoursValidFor, 'hour').isBefore(moment(claimedAt));
            }

            return true;
        });

        const discountsByProductId = {};

        for (const discount of discounts) {
            for (const discountProduct of discount.discountProducts) {
                const { product, percent } = discountProduct;

                discountsByProductId[product.id] = {
                    ...withoutRelations(discount),
                    percent: percent ?? discount.percent,
                };
            }
        }

        setDiscountData((data) => {
            data.discounts = discounts;
            data.discountByProductId = discountsByProductId;
        });
    });

    return {
        state,
        useRegister() {
            useEffect(() => {
                apiQueryDiscounts.run();
            }, [discountIds.join()]);
        },
        getDiscountByProductId(productId: number): Discount | undefined {
            return discountData.discountByProductId?.[productId];
        },
        claimDiscount(discount: Discount) {
            const discountId = discount.id;

            setState((state) => {
                state.discountIds = state.discountIds || [];
                state.discountIdClaimedAt = state.discountIdClaimedAt || {};

                if (!state.discountIds.includes(discountId)) {
                    state.discountIds.push(discountId);
                }

                state.discountIdClaimedAt[discountId] = moment().format();
            });
        },
    };
}
